import { useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiErr } from "./SuperHeader";

export const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800", disabled: "bg-slate-200 text-slate-700", deleted: "bg-slate-800 text-white",
};

// action config: label, endpoint, needs reason?, body text
const ACTIONS = {
  approve: { label: "Approve", cls: "text-emerald-700 hover:bg-emerald-50", btn: "bg-emerald-600 hover:bg-emerald-700", body: "The Company Admin will be emailed and can sign in immediately." },
  reject: { label: "Reject", cls: "text-rose-700 hover:bg-rose-50", btn: "bg-rose-600 hover:bg-rose-700", reason: "required", body: "The applicant will be emailed this reason. Rejection is final." },
  disable: { label: "Disable", cls: "text-rose-700 hover:bg-rose-50", btn: "bg-rose-600 hover:bg-rose-700", reason: "optional", body: "All users are signed out and blocked, campaigns pause and AI replies stop. No data is deleted." },
  enable: { label: "Enable", cls: "text-emerald-700 hover:bg-emerald-50", btn: "bg-emerald-600 hover:bg-emerald-700", body: "Users of this company can sign in again." },
};
const BY_STATUS = { pending: ["approve", "reject"], approved: ["disable"], disabled: ["enable"], rejected: [] };

function ActionDialog({ company, action, onClose, onDone }) {
  const cfg = ACTIONS[action];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    if (cfg.reason === "required" && reason.trim().length < 3) return toast.error("Please give a reason (at least 3 characters)");
    setBusy(true);
    try {
      await api.post(`/admin/companies/${company.id}/${action}`, cfg.reason ? { reason: reason.trim() || null } : {});
      toast.success(`${company.name}: ${{ approve: "approved", reject: "rejected", disable: "disabled", enable: "enabled" }[action]}`); onClose(); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-white" onClick={(e) => e.stopPropagation()} data-testid="status-confirm-dialog">
        <DialogHeader><DialogTitle>{cfg.label} {company.name}?</DialogTitle><DialogDescription>{cfg.body}</DialogDescription></DialogHeader>
        {cfg.reason && <Textarea placeholder={cfg.reason === "required" ? "Reason (required, sent to the applicant)" : "Reason (optional, saved in the activity log)"} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} data-testid="status-reason-input" />}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose} data-testid="status-cancel">Cancel</Button>
          <Button onClick={run} disabled={busy} className={cfg.btn} data-testid="status-confirm">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{cfg.label}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ company, onClose, onDone }) {
  const [f, setF] = useState({ backup: "yes", password: "", text: "" });
  const [busy, setBusy] = useState(false);
  const quick = f.backup === "quick";
  const ready = quick || (f.text === "DELETE" && f.password.length > 0);
  const run = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const body = quick ? { mode: "quick", confirm_company_id: company.id }
        : { mode: "secure", password: f.password, confirm_text: f.text, confirm_company_id: company.id, backup: f.backup === "yes" };
      const { data } = await api.post(`/admin/companies/${company.id}/delete`, body);
      toast.success(data.backup_id ? `${company.name} deleted · backup kept 30 days` : `${company.name} permanently deleted`);
      onClose(); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-white" onClick={(e) => e.stopPropagation()} data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" />Permanently delete company</DialogTitle>
          <DialogDescription>This erases all users, contacts, leads, conversations, messages, campaigns, templates, settings and WhatsApp configuration of this company. {f.backup === "no" ? "It cannot be undone." : "A downloadable backup is kept for 30 days, then erased."}</DialogDescription>
        </DialogHeader>
        <div className="p-3 rounded-lg bg-rose-50 border border-rose-100 text-sm">
          <div className="font-semibold" data-testid="delete-company-name">{company.name}</div>
          <div className="font-mono text-xs text-slate-600" data-testid="delete-company-id">ID: {company.id}</div>
        </div>
        <RadioGroup value={f.backup} onValueChange={(v) => setF({ ...f, backup: v })} className="space-y-1">
          <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="yes" data-testid="delete-backup-yes" />Delete with 30-day backup <span className="text-xs text-slate-500">(downloadable, then erased)</span></label>
          <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="no" data-testid="delete-backup-no" />Delete with no backup</label>
          <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="quick" data-testid="delete-quick" />Quick delete — just click OK <span className="text-xs text-slate-500">(30-day backup kept)</span></label>
        </RadioGroup>
        {!quick && <>
          <Input type="password" placeholder="Your Super Admin password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="delete-password-input" />
          <Input placeholder='Type DELETE to confirm' value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="delete-confirm-input" />
        </>}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose} data-testid="delete-cancel">Cancel</Button>
          <Button onClick={run} disabled={!ready || busy} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{quick ? "OK, delete" : "Delete permanently"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StatusAction({ company, onDone, size = "sm" }) {
  const [open, setOpen] = useState(null);
  if (company.status === "deleted") return null;
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <span className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
      {(BY_STATUS[company.status] || []).map((a) => (
        <Button key={a} size={size} variant="outline" className={ACTIONS[a].cls} onClick={stop(() => setOpen(a))} data-testid={`${a}-${company.id}`}>{ACTIONS[a].label}</Button>
      ))}
      <Button size={size} variant="outline" className="text-rose-700 hover:bg-rose-50" onClick={stop(() => setOpen("delete"))} data-testid={`delete-${company.id}`}>Delete</Button>
      {open && open !== "delete" && <ActionDialog company={company} action={open} onClose={() => setOpen(null)} onDone={onDone} />}
      {open === "delete" && <DeleteDialog company={company} onClose={() => setOpen(null)} onDone={onDone} />}
    </span>
  );
}
